import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Zap, Star, ShieldCheck, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Vinci Pulse-Point — a 3-slider, sub-15-second feedback experience.
 * Cyan/gold glow on obsidian.  Background pulses to the pulse average.
 * Fires POST /api/feedback/pulse on Sync Feedback then plays a 2-second
 * "Recalibrating Ecosystem" ripple before flashing the 5151 achievement badge.
 */

const AXES = [
  { key: "logistics", label: "Logistics Pulse",   sub: "Speed",       color: "#22D3EE", accent: "#06B6D4", Icon: Zap },
  { key: "honor",     label: "Material Honor",    sub: "Quality",     color: "#D8B23E", accent: "#B87333", Icon: Star },
  { key: "trust",     label: "Network Trust",     sub: "Reliability", color: "#3DBB84", accent: "#2FB999", Icon: ShieldCheck },
];

const Slider = ({ axis, value, onChange }) => {
  const pct = ((value - 0) / 100) * 100;
  return (
    <div className="relative py-4">
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-center gap-2">
          <axis.Icon size={14} style={{ color: axis.color }} />
          <span className="font-grotesk text-[10px] uppercase tracking-[0.28em]" style={{ color: axis.color }}>{axis.label}</span>
          <span className="font-body text-[10px] text-[#6f6f68]">· {axis.sub}</span>
        </div>
        <span className="font-serif text-3xl tabular-nums" style={{ color: axis.color, textShadow: `0 0 14px ${axis.color}80` }}>{value}</span>
      </div>
      <div className="relative h-8 flex items-center">
        <div className="absolute inset-x-0 h-[2px]" style={{
          background: `linear-gradient(to right, ${axis.accent}22 0%, ${axis.color}66 ${pct}%, #ffffff10 ${pct}%, #ffffff10 100%)`,
        }}/>
        <div className="absolute h-[1px] w-full opacity-30" style={{
          background: `repeating-linear-gradient(to right, ${axis.color} 0 2px, transparent 2px 12px)`,
        }}/>
        <input type="range" min={0} max={100} step={1} value={value}
               onChange={(e) => onChange(Number(e.target.value))}
               data-testid={`pulse-slider-${axis.key}`}
               className="w-full appearance-none bg-transparent relative z-10
                          [&::-webkit-slider-thumb]:appearance-none
                          [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6
                          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
                          [&::-webkit-slider-thumb]:border-2"
               style={{
                 "--tw-thumb-bg": axis.color,
               }}/>
        <div className="pointer-events-none absolute -translate-x-1/2 flex items-center justify-center"
             style={{ left: `${pct}%` }}>
          <div className="w-6 h-6 rounded-full" style={{
            background: axis.color,
            boxShadow: `0 0 0 2px ${axis.color}44, 0 0 22px ${axis.color}cc, 0 0 46px ${axis.color}66`,
          }}/>
        </div>
      </div>
    </div>
  );
};

export function VinciPulsePoint({ orderId, orderRef, onClose, autoOpen = false }) {
  const { user, api } = useAuth();
  const [open, setOpen] = useState(autoOpen);
  const [scores, setScores] = useState({ logistics: 78, honor: 82, trust: 75 });
  const [phase, setPhase] = useState("interact");   // interact · sync · unlocked
  const timerRef = useRef(null);

  const avg = useMemo(() => Math.round((scores.logistics + scores.honor + scores.trust) / 3), [scores]);
  const dominant = useMemo(() => {
    // Blend the three axis hues weighted by their score.
    const items = [
      { c: [34, 211, 238], w: scores.logistics },
      { c: [216, 178, 62], w: scores.honor },
      { c: [61, 187, 132], w: scores.trust },
    ];
    const sum = items.reduce((s, i) => s + i.w, 0) || 1;
    const r = items.reduce((s, i) => s + i.c[0] * i.w, 0) / sum;
    const g = items.reduce((s, i) => s + i.c[1] * i.w, 0) / sum;
    const b = items.reduce((s, i) => s + i.c[2] * i.w, 0) / sum;
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, 0.28)`;
  }, [scores]);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const sync = async () => {
    setPhase("sync");
    try {
      await api.post("/feedback/pulse", {
        order_id: orderId || null,
        order_ref: orderRef || null,
        logistics: scores.logistics, honor: scores.honor, trust: scores.trust,
      });
    } catch { /* offline-safe · never block the animation */ }
    timerRef.current = setTimeout(() => setPhase("unlocked"), 2000);
    timerRef.current = setTimeout(() => {}, 200);
  };

  const close = () => {
    setOpen(false);
    setPhase("interact");
    if (onClose) onClose();
  };

  return (
    <>
      {!autoOpen && (
        <button data-testid="pulse-trigger" onClick={() => setOpen(true)}
                className="group relative overflow-hidden rounded-sm px-6 py-3 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#22D3EE] border border-[#22D3EE]/40 hover:border-[#22D3EE]">
          <span className="absolute inset-0 bg-[#22D3EE]/10 opacity-0 group-hover:opacity-100 transition-opacity"/>
          <span className="relative flex items-center gap-2"><Zap size={12}/> Sync your Pulse-Point</span>
        </button>
      )}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="pulse-modal"
            className="fixed inset-0 z-[80] grid place-items-center"
            style={{ backdropFilter: "blur(28px)" }}>
            <motion.div className="absolute inset-0"
                        animate={{ backgroundColor: dominant }}
                        transition={{ duration: 0.6 }}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#020204_70%)]"/>
              {phase === "interact" && (
                <motion.div className="absolute inset-0 pointer-events-none"
                            animate={{ opacity: [0.2, 0.7, 0.2] }}
                            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                            style={{
                              background: `radial-gradient(circle at 50% 50%, ${dominant} 0%, transparent 60%)`,
                            }}/>
              )}
            </motion.div>

            <motion.div initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92 }}
                        className="relative w-[min(560px,92vw)] rounded-sm border border-white/10 bg-[#040407] p-8"
                        style={{ boxShadow: `0 0 80px ${dominant}, inset 0 0 0 1px rgba(255,255,255,0.02)` }}>
              <button data-testid="pulse-close" onClick={close}
                      className="absolute top-4 right-4 text-[#8A8A82] hover:text-[#F5F5F0]">
                <X size={16}/>
              </button>

              {phase === "interact" && (
                <>
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#22D3EE]">The Vinci Pulse-Point</div>
                  <h2 className="font-serif text-3xl text-[#F5F5F0] mt-2">3 slides · 15 seconds · zero forms.</h2>
                  <p className="font-body text-[12px] text-[#8A8A82] mt-2 leading-relaxed">
                    Slide each pulse to how the trade felt. The background recalibrates as you slide. When you sync, the ecosystem adjusts and your <b className="text-[#D8B23E]">5151-series profile</b> earns an achievement.
                  </p>
                  <div className="mt-6 space-y-1">
                    {AXES.map(a => (
                      <Slider key={a.key} axis={a}
                              value={scores[a.key]}
                              onChange={(v) => setScores(s => ({ ...s, [a.key]: v }))}/>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <div className="font-body text-[11px] text-[#8A8A82]">
                      Pulse average · <span className="text-[#F5F5F0] font-serif text-lg tabular-nums">{avg}</span>
                    </div>
                    <button data-testid="pulse-sync" onClick={sync}
                            className="relative overflow-hidden rounded-sm px-6 py-3 font-grotesk text-[10px] uppercase tracking-[0.28em] text-black bg-[#D8B23E]"
                            style={{ boxShadow: `0 0 30px ${dominant}` }}>
                      Sync feedback
                    </button>
                  </div>
                </>
              )}

              {phase === "sync" && (
                <div className="min-h-[320px] flex flex-col items-center justify-center gap-5 text-center">
                  <motion.div className="w-24 h-24 rounded-full border-2 border-[#22D3EE]"
                              animate={{ scale: [1, 1.6, 1], opacity: [1, 0.2, 1] }}
                              transition={{ duration: 1, repeat: Infinity }}
                              style={{ boxShadow: `0 0 60px #22D3EE99` }}/>
                  <div>
                    <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#22D3EE]">Please wait</div>
                    <div className="font-serif text-2xl text-[#F5F5F0] mt-1">Recalibrating the ecosystem…</div>
                  </div>
                </div>
              )}

              {phase === "unlocked" && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                            className="min-h-[320px] flex flex-col items-center justify-center gap-4 text-center"
                            data-testid="pulse-unlocked">
                  <motion.div initial={{ rotate: -20, scale: 0.4 }} animate={{ rotate: 0, scale: 1 }}
                              transition={{ type: "spring", stiffness: 150 }}
                              className="relative">
                    <div className="w-28 h-28 rounded-full grid place-items-center border-2 border-[#D8B23E]"
                         style={{ background: "radial-gradient(circle,#D8B23E22 0%,transparent 70%)",
                                   boxShadow: "0 0 60px #D8B23Ecc" }}>
                      <CheckCircle2 size={44} className="text-[#D8B23E]"/>
                    </div>
                    <motion.div className="absolute inset-0 rounded-full border border-[#D8B23E]"
                                animate={{ scale: [1, 1.8], opacity: [0.8, 0] }}
                                transition={{ duration: 1.6, repeat: Infinity }}/>
                  </motion.div>
                  <div>
                    <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E]">Achievement unlocked</div>
                    <div className="font-serif text-3xl text-[#F5F5F0] mt-1">Pulse-Point Calibrator</div>
                    <div className="font-body text-[12px] text-[#8A8A82] mt-1">
                      Awarded to your <b className="text-[#D8B23E]">5151-series</b> profile · {user?.vinci_code || "VNC-5151-####"} · pulse avg <b className="text-[#F5F5F0]">{avg}</b>
                    </div>
                  </div>
                  <button data-testid="pulse-done" onClick={close}
                          className="rounded-sm border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em]">
                    Close · Return to trade
                  </button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
